// frontend/src/components/UI/DismissibleToastContent.js
import React, { useRef, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import toast from '../../utils/toast';
import { resolveToastIconSlot } from '../../utils/toastMessage';

/**
 * DismissibleToastContent
 * 
 * Renders inside <ToastBar> to add dismiss interactions to every toast.
 * Applied at the Toaster level so ALL toasts are dismissable regardless of import path.
 * 
 * Features:
 *   - Close (X) button (except on loading toasts)
 *   - Click-anywhere-to-dismiss (except on loading toasts)
 *   - Swipe-to-dismiss on mobile (cross-platform: iOS + Android)
 * 
 * Cross-platform handling:
 *   - iOS Safari: Native touch listeners with { passive: false } for preventDefault()
 *   - iOS PWA: Edge-swipe exclusion zone (30px) to avoid conflict with back navigation
 *   - iOS: Rubber-band overscroll isolation via axis-locking within first 10px
 *   - Android: touchcancel treated as abort (spring-back, not dismiss)
 *   - Android: touch-action: pan-y via CSS allows vertical scroll pass-through
 *   - Universal: Multi-touch rejection, GPU-accelerated transforms, axis-locking
 */

// =============================================================================
// Constants
// =============================================================================
const SWIPE_THRESHOLD_PX = 50;          // Min horizontal distance to trigger dismiss
const SWIPE_MAX_DURATION_MS = 300;      // Max swipe duration to count as intentional
const EDGE_ZONE_PX = 30;               // Ignore swipes near screen edges (iOS PWA back gesture)
const DISMISS_SLIDE_PX = 300;           // Slide-out distance on dismiss
const DISMISS_ANIMATION_MS = 200;       // Slide-out animation duration
const SPRING_BACK_MS = 150;             // Spring-back animation duration
const OPACITY_FADE_DIVISOR = 150;       // deltaX divisor for proportional opacity fade

// =============================================================================
// Component
// =============================================================================

const TOAST_FALLBACK_ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const DismissibleToastContent = ({ t, icon, message }) => {
  const containerRef = useRef(null);
  const isLoading = t.type === 'loading';
  const { slot, icon: resolvedIcon } = resolveToastIconSlot({
    type: t.type,
    className: t.className,
    icon,
  });
  const FallbackIcon = TOAST_FALLBACK_ICONS[slot];
  
  const touchState = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false,
    axisLocked: false,
    isHorizontal: false,
  });

  const handleDismiss = useCallback(() => {
    if (isLoading) return; // Never dismiss loading toasts
    toast.dismiss(t.id);
  }, [t.id, isLoading]);

  // Animate slide-out then dismiss
  const animateDismiss = useCallback((direction) => {
    const el = containerRef.current;
    if (!el) {
      handleDismiss();
      return;
    }
    el.style.transition = `transform ${DISMISS_ANIMATION_MS}ms ease-out, opacity ${DISMISS_ANIMATION_MS}ms ease-out`;
    el.style.transform = `translateX(${direction > 0 ? DISMISS_SLIDE_PX : -DISMISS_SLIDE_PX}px)`;
    el.style.opacity = '0';
    setTimeout(handleDismiss, DISMISS_ANIMATION_MS);
  }, [handleDismiss]);

  // Animate spring-back to original position
  const animateSpringBack = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = `transform ${SPRING_BACK_MS}ms ease-out, opacity ${SPRING_BACK_MS}ms ease-out`;
    el.style.transform = 'translateX(0)';
    el.style.opacity = '1';
  }, []);

  // =========================================================================
  // Swipe-to-Dismiss (native touch events for iOS compatibility)
  // =========================================================================
  useEffect(() => {
    const el = containerRef.current;
    if (!el || isLoading) return; // No swipe on loading toasts

    const onTouchStart = (e) => {
      // Reject multi-touch (pinch/zoom)
      if (e.touches.length > 1) {
        touchState.current.isTracking = false;
        return;
      }

      const touch = e.touches[0];

      // iOS PWA edge-swipe exclusion: ignore swipes near screen edges
      // Prevents conflict with iOS back/forward navigation gestures
      if (touch.clientX < EDGE_ZONE_PX || touch.clientX > window.innerWidth - EDGE_ZONE_PX) {
        touchState.current.isTracking = false;
        return;
      }

      // Clear any residual transition from previous interaction
      el.style.transition = 'none';

      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isTracking: true,
        axisLocked: false,
        isHorizontal: false,
      };
    };

    const onTouchMove = (e) => {
      if (!touchState.current.isTracking) return;

      // Abort if multi-touch appeared mid-swipe
      if (e.touches.length > 1) {
        touchState.current.isTracking = false;
        animateSpringBack();
        return;
      }

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchState.current.startX;
      const deltaY = touch.clientY - touchState.current.startY;

      // Axis locking: determine swipe direction within the first ~10px of movement
      // This prevents iOS rubber-band overscroll from corrupting the gesture
      if (!touchState.current.axisLocked) {
        const absDX = Math.abs(deltaX);
        const absDY = Math.abs(deltaY);

        // Wait for enough movement to reliably determine axis
        if (absDX < 5 && absDY < 5) return;

        touchState.current.axisLocked = true;
        touchState.current.isHorizontal = absDX > absDY;

        // If vertical-dominant: abort, let browser handle scroll/pull-to-refresh
        if (!touchState.current.isHorizontal) {
          touchState.current.isTracking = false;
          return;
        }
      }

      // Horizontal swipe: prevent page scroll and apply visual feedback
      if (touchState.current.isHorizontal) {
        // CRITICAL: preventDefault() requires { passive: false } on iOS Safari
        e.preventDefault();

        // GPU-accelerated transform for smooth 60fps
        el.style.transform = `translateX(${deltaX}px)`;
        // Proportional opacity fade
        const opacity = 1 - Math.min(Math.abs(deltaX) / OPACITY_FADE_DIVISOR, 0.6);
        el.style.opacity = String(opacity);
      }
    };

    const onTouchEnd = (e) => {
      if (!touchState.current.isTracking || !touchState.current.isHorizontal) {
        touchState.current.isTracking = false;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchState.current.startX;
      const duration = Date.now() - touchState.current.startTime;

      touchState.current.isTracking = false;

      // Dismiss if: distance > threshold AND swipe was fast enough
      if (Math.abs(deltaX) > SWIPE_THRESHOLD_PX && duration < SWIPE_MAX_DURATION_MS) {
        animateDismiss(deltaX > 0 ? 1 : -1);
      } else {
        // Spring back to original position
        animateSpringBack();
      }
    };

    // Android: touchcancel fires when browser takes over gesture
    // (pull-to-refresh, scroll takeover, etc.)
    // ALWAYS treat as abort -- spring back, never dismiss
    const onTouchCancel = () => {
      touchState.current.isTracking = false;
      animateSpringBack();
    };

    // Register native event listeners
    // touchmove MUST be { passive: false } for iOS Safari preventDefault() to work
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [isLoading, animateDismiss, animateSpringBack]);

  return (
    <div
      ref={containerRef}
      className="toast-dismissible"
      onClick={isLoading ? undefined : handleDismiss}
      role="alert"
      aria-live="polite"
    >
      {slot === 'provided' && resolvedIcon ? (
        <span className="toast-icon" aria-hidden="true">{resolvedIcon}</span>
      ) : null}
      {FallbackIcon ? (
        <span className="toast-icon" aria-hidden="true">
          <FallbackIcon className="w-5 h-5" strokeWidth={2.25} />
        </span>
      ) : null}
      <span className="toast-dismissible-content">{message}</span>
      {!isLoading && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          className="toast-dismiss-btn"
          aria-label="Dismiss notification"
          tabIndex={0}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default DismissibleToastContent;
